// NotesApp source file: trash.util.ts
//
// Trash / soft delete (tasks/delete_trash_client.md). The backend decides everything about deletion
// and purging; the helpers here only cover the client's own local state and informational display.

import type { Note } from '../_service/data.service';

/**
 * Thrown by DataRemoteService.updateNote() when a push hits a note that has meanwhile been moved to
 * Trash (or purged) on the server: the push must drop the stale local copy, never re-create it.
 */
export class NoteDeletedOnServerError extends Error {
  readonly status = 404;
  constructor(readonly noteId: string) {
    super(`Note ${noteId} was deleted on the server`);
    this.name = 'NoteDeletedOnServerError';
  }
}

/** Server-side retention before a trashed note is purged (backend TRASH_RETENTION_DAYS). Display only. */
export const TRASH_RETENTION_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * True for a note the user deleted on THIS device whose deletion hasn't reached the server yet — the
 * local tombstone (syncStatus 'deleted') that SyncCoordinatorService pushes as a DELETE. Such a note
 * is already "in Trash" as far as the user is concerned, so normal views never show it.
 */
export function isPendingLocalDeletion(note: Pick<Note, 'syncStatus'> | null | undefined): boolean {
  return note?.syncStatus === 'deleted';
}

/** "Deleted today" / "Deleted yesterday" / "Deleted 3 days ago" / "Deleted 15 Sep 2026". */
export function formatDeletedAgo(deletedAt: string | null | undefined, now: Date = new Date()): string {
  const deleted = parseDate(deletedAt);
  if (!deleted) return 'Deleted';
  const days = calendarDaysBetween(deleted, now);
  if (days <= 0) return 'Deleted today';
  if (days === 1) return 'Deleted yesterday';
  if (days < 7) return `Deleted ${days} days ago`;
  return `Deleted ${deleted.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/**
 * Whole days left before the server's automatic purge (never negative). Informational only — the
 * backend's purge job is what actually deletes, on its own schedule.
 */
export function trashDaysRemaining(deletedAt: string | null | undefined, now: Date = new Date()): number | null {
  const deleted = parseDate(deletedAt);
  if (!deleted) return null;
  const purgeAt = deleted.getTime() + TRASH_RETENTION_DAYS * DAY_MS;
  return Math.max(0, Math.ceil((purgeAt - now.getTime()) / DAY_MS));
}

/** "Permanently deleted in 17 days" / "… in 1 day" / "Permanently deleted soon". */
export function formatRetentionRemaining(deletedAt: string | null | undefined, now: Date = new Date()): string {
  const days = trashDaysRemaining(deletedAt, now);
  if (days === null) return '';
  if (days <= 0) return 'Permanently deleted soon';
  return `Permanently deleted in ${days} ${days === 1 ? 'day' : 'days'}`;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function calendarDaysBetween(from: Date, to: Date): number {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((end - start) / DAY_MS);
}
